import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';

import { request } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  intervalSchema,
  planKeySchema,
  type BillingInterval,
  type PlanKey,
} from '@/lib/plan-catalog';
import { sessionSchema } from '@/lib/schemas';

/* ---------------------------------------------------------------------------
   חיוב ומנוי — החוזה מול src/modules/billing בשרת.
   סכומים באגורות (מספר שלם), לפני מע"מ אלא אם השם אומר total.
   --------------------------------------------------------------------------- */

const roleSchema = z.enum(['OWNER', 'MANAGER', 'FIELD', 'VIEWER']);
export type SeatRole = z.infer<typeof roleSchema>;

const breakdownSchema = z.array(
  z.object({ role: roleSchema, count: z.number(), weight: z.number(), seats: z.number() }),
);

const chargeSchema = z.object({
  listMinor: z.number(),
  discountMinor: z.number(),
  amountBeforeVatMinor: z.number(),
  vatMinor: z.number(),
  totalMinor: z.number(),
});
export type ChargeView = z.infer<typeof chargeSchema>;

export const subscriptionStatusSchema = z.enum([
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'EXPIRED',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const overviewSchema = z.object({
  canManage: z.boolean(),
  paymentsAvailable: z.boolean(),
  provider: z.string().nullable(),
  issuesTaxInvoices: z.boolean(),
  catalogVersion: z.string(),
  ai: z.object({
    spentMinor: z.number(),
    budgetMinor: z.number(),
    source: z.string(),
  }),
  subscription: z
    .object({
      plan: planKeySchema,
      interval: intervalSchema,
      status: subscriptionStatusSchema,
      readOnly: z.boolean(),
      readOnlyReason: z.string().nullable(),
      trialEndsAt: z.string().nullable(),
      trialDaysLeft: z.number().nullable(),
      currentPeriodStart: z.string().nullable(),
      currentPeriodEnd: z.string().nullable(),
      cancelAtPeriodEnd: z.boolean(),
      graceEndsAt: z.string().nullable(),
      extraSeats: z.number().int(),
      complimentary: z.boolean(),
      coupon: z.object({ percent: z.number(), monthsLeft: z.number() }).nullable(),
      pending: z
        .object({
          plan: planKeySchema,
          interval: intervalSchema,
          extraSeats: z.number().int(),
          effectiveAt: z.string().nullable(),
        })
        .nullable(),
      amountDueMinor: z.number().nullable(),
    })
    .nullable(),
  seats: z.object({
    used: z.number(),
    limit: z.number().nullable(),
    breakdown: breakdownSchema,
  }),
  nextInvoice: chargeSchema
    .extend({
      date: z.string(),
      plan: planKeySchema,
      interval: intervalSchema,
      extraSeats: z.number().int(),
    })
    .nullable(),
});
export type BillingOverview = z.infer<typeof overviewSchema>;
export type Subscription = NonNullable<BillingOverview['subscription']>;

export const blockerSchema = z.object({
  type: z.literal('seats'),
  seatsUsed: z.number(),
  seatLimit: z.number(),
  excess: z.number(),
  breakdown: breakdownSchema,
});
export type Blocker = z.infer<typeof blockerSchema>;

export const previewSchema = z.object({
  kind: z.enum(['none', 'upgrade', 'downgrade']),
  effectiveAt: z.string().nullable(),
  due: chargeSchema,
  creditMinor: z.number(),
  newPeriod: chargeSchema,
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  blockers: z.array(blockerSchema),
});
export type ChangePreview = z.infer<typeof previewSchema>;

export const checkoutResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('active') }),
  z.object({ status: z.literal('redirect'), redirectUrl: z.string() }),
]);

export const changeResultSchema = z
  .object({
    kind: z.enum(['none', 'upgrade', 'downgrade']),
    effectiveAt: z.string().nullable().optional(),
  })
  .passthrough();

export const invoiceSchema = z.object({
  id: z.string(),
  /** מספר רץ (Int בשרת) — מוצג כמחרוזת. */
  number: z.union([z.string(), z.number()]).transform(String),
  kind: z.enum(['SUBSCRIPTION', 'PRORATION']).catch('SUBSCRIPTION'),
  plan: planKeySchema,
  interval: intervalSchema,
  extraSeats: z.number().int(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  listAmount: z.string(),
  discountAmount: z.string(),
  amountBeforeVat: z.string(),
  vatAmount: z.string(),
  totalAmount: z.string(),
  currency: z.string(),
  status: z.enum(['PAID', 'OPEN', 'VOID', 'FAILED']),
  failureReason: z.string().nullable(),
  issuedAt: z.string(),
  paidAt: z.string().nullable(),
  provider: z.string().nullable(),
  demo: z.boolean(),
});
export type BillingInvoice = z.infer<typeof invoiceSchema>;

/** פרטי SEAT_LIMIT מ-POST /team ו-PATCH /team/:id. */
export const seatLimitDetailsSchema = z.object({
  seatsUsed: z.number(),
  seatsAfter: z.number(),
  seatLimit: z.number(),
  plan: z.string().optional(),
});
export type SeatLimitDetails = z.infer<typeof seatLimitDetailsSchema>;

export const BILLING_KEY = ['billing', 'overview'] as const;

export function useBillingOverview(enabled = true) {
  return useQuery({
    queryKey: BILLING_KEY,
    queryFn: ({ signal }) => request('/billing', { schema: overviewSchema, signal }),
    enabled,
  });
}

export function useBillingInvoices(enabled = true) {
  return useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: ({ signal }) =>
      request('/billing/invoices', { schema: z.array(invoiceSchema), signal }),
    enabled,
  });
}

export interface Selection {
  plan: PlanKey;
  interval: BillingInterval;
  extraSeats: number;
}

export function usePreviewChange(sel: Selection | null) {
  return useQuery({
    queryKey: ['billing', 'preview', sel?.plan, sel?.interval, sel?.extraSeats],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        plan: sel!.plan,
        interval: sel!.interval,
        extraSeats: String(sel!.extraSeats),
      });
      return request(`/billing/preview-change?${qs.toString()}`, { schema: previewSchema, signal });
    },
    enabled: sel !== null,
    staleTime: 0,
  });
}

/** מרענן את בלוק החיוב בסשן (הבאנרים) אחרי שינוי במנוי. */
export function useRefreshSessionBilling() {
  const { updateSession } = useAuth();
  return useCallback(async () => {
    try {
      const s = await request('/auth/me', { schema: sessionSchema });
      updateSession({ billing: s.billing ?? null });
    } catch {
      /* הבאנר יתעדכן בכניסה הבאה */
    }
  }, [updateSession]);
}

/** סדר התוכניות — לתווית "שדרוג" / "מעבר". */
export const PLAN_ORDER: PlanKey[] = ['STARTER', 'TEAM', 'GROWTH', 'BUSINESS'];

/** "12.50" (מחרוזת עשרונית מהשרת) → אגורות. */
export const decimalToMinor = (v: string): number => Math.round(Number.parseFloat(v) * 100);
